import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';


import React from 'react';
import { continueWatchingTemplates } from './continueWatchingTemplates';
import { Check, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ContinueWatchingTemplateSelectorProps {
  currentTemplate: string;
  useTemplate: boolean;
  onSelectTemplate: (templateId: string) => void;
  onTestOverlay?: () => void;
}

export const ContinueWatchingTemplateSelector: React.FC<ContinueWatchingTemplateSelectorProps> = ({
  currentTemplate,
  useTemplate,
  onSelectTemplate,
  onTestOverlay
}) => {
  useLanguage();
  // Lógica correta de seleção: template só está selecionado se useTemplate=true E corresponde ao ID
  const isTemplateSelected = (templateId: string) => currentTemplate === templateId && useTemplate;

  const handleTemplateSelect = (templateId: string) => {
    // onSelectTemplate já seta useTemplate: true, não precisa chamar onToggleUseTemplate
    onSelectTemplate(templateId);
  };

  return (
    <div className="space-y-4">
      {/* Grid 2 Templates */}
      <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label={translateInterfaceText("Selecionar template")}>
        {continueWatchingTemplates.map((template) => {
          const isSelected = isTemplateSelected(template.id);
          return (
            <div
              key={template.id}
              role="radio"
              tabIndex={0}
              aria-checked={isSelected}
              aria-label={template.name}
              className={cn(
                "relative overflow-hidden cursor-pointer rounded-xl",
                "transition-all duration-200 ease-out",
                "focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30",
                isSelected
                  ? 'border-2 border-[#2563EB] bg-[#EFF6FF] shadow-[0_0_0_1px_#2563EB]'
                  : 'border border-[#E6E6F0] bg-white hover:border-[#2563EB]/40 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]'
              )}
              onClick={() => handleTemplateSelect(template.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTemplateSelect(template.id);
                }
              }}
            >
              {isSelected && (
                <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-[#2563EB] rounded-full flex items-center justify-center shadow-sm z-10">
                  <Check size={12} className="text-white" strokeWidth={3} />
                </div>
              )}

              {/* Preview interno colorido */}
              <div className="p-2.5">
                <div
                  className="aspect-video w-full flex flex-col items-center justify-center gap-1.5 rounded-lg overflow-hidden"
                >
                  {template.preview && template.preview.includes('.svg') ? (
                    <img
                      src={template.preview}
                      alt={template.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <>
                      <Play className="w-5 h-5" style={{ color: template.textColor }} />
                      <p
                        className={cn(
                          "text-[10px] transition-all duration-200",
                          isSelected ? "font-bold" : "font-semibold"
                        )}
                        style={{ color: isSelected ? '#2563EB' : template.textColor }}
                      >
                        {template.name}
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
